'use client';

//region Imports
import React, {useEffect, useRef, useState} from 'react';
import {Application} from '@splinetool/runtime';
import {clamp} from 'three/src/math/MathUtils.js';
import _ from 'lodash';
//endregion Imports

//region Types & Interfaces
interface XYZ {
  x: number;
  y: number;
  z: number;
}

interface ObjectVisibility {
  show: Array<string>;
  hide: Array<string>;
}

interface SceneSettings {
  objects: ObjectVisibility;
  position: XYZ;
  minX: number;
  maxX: number;
  fov: number;
  wheelSensitivity: number;
  dragSensitivity: number;
}

interface SplineProps {
  windowState: {
    orientation: 'portrait' | 'landscape';
    width: number;
    height: number;
  };
  settings: {
    url: string;
    portrait: SceneSettings;
    landscape: SceneSettings;
    //geometry: GeometrySettings;
  };
  onLoad?: (
    app: Application,
    canvasRef: React.RefObject<HTMLCanvasElement>
  ) => void;
  style?: React.CSSProperties;
  className?: string;
}

type SplineObject = {
  visible?: boolean;
  children?: SplineObject[];
};
//endregion Types & Interfaces

const Spline: React.FC<SplineProps> = ({
                                         windowState,
                                         settings,
                                         onLoad,
                                         style,
                                         className
                                       }) => {
  //region Variables
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const appRef = useRef<Application | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const [canvasKey, setCanvasKey] = useState(0);
  //endregion Variables

  //region Methods & Event Handlers
  const getCamera = (app: any) => {
    return (
      app?._camera ??
      app?._controls?._camera ??
      app?._scene?.children?.find((o: any) => o.type === 'Camera') ??
      null
    );
  };

  const setObjectVisibility = (app: Application) => {
    if (!app) return null;
    const objects = settings[windowState.orientation].objects;

    _.forEach(objects, (object, key) => {
      const visible = (key === "show");

      _.forEach(object, (name) => {
        const o = app.findObjectByName(name);
        if (o) {
          if (name.includes("group")) {
            setGroupVisibility(o, visible)

          } else {
            o.visible = visible;
          }
        }
      });
    });
  };

  const setGroupVisibility = (group: SplineObject | null, visible: boolean) => {
    if (!group) return;

    group.visible = visible;

    if (group.children && group.children.length) {
      group.children.forEach(child => setGroupVisibility(child, visible));
    }
  };

  const triggerSplineEvent = (app: any, target: string) => {
    if (!app) return;

    requestAnimationFrame(() => {
      app.emitEventReverse?.('mouseDown', target);

      requestAnimationFrame(() => {
        app.emitEvent?.('mouseDown', target);
      });
    });
  };

  const setCamera = (position: XYZ) => {
    const cam = getCamera(appRef.current);
    if (!cam) return;

    const {minX, maxX} = settings[windowState.orientation];
    cam.position.x = clamp(position.x, minX, maxX);
    cam.position.y = position.y;
    cam.position.z = position.z;
    cam.matrixWorldNeedsUpdate = true;
    cam.updateMatrix();
    cam.updateMatrixWorld();
    cam.updateProjectionMatrix();
  };

  const switchLayout = (app: any, orientation: 'portrait' | 'landscape') => {
    setObjectVisibility(app);

    const target =
      orientation === 'portrait'
        ? 'portraitController'
        : 'landscapeController';

    triggerSplineEvent(app, target);

    const t = setTimeout(() => {
      const cam = getCamera(appRef.current);
      if (cam) {
        requestAnimationFrame(() => {
          const {position} = settings[orientation];
          setCamera(position);
          // console.log('switchLayout() orientation', orientation);
        });

        if (!initializedRef.current) {
          initializedRef.current = true;
          onLoad?.(app, canvasRef);
        }
      }
      clearTimeout(t);
    }, 50)
  };

  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      setTimeout(() => setCanvasKey(prev => prev + 1), 50);
    }
  };
  //endregion Methods & Event Handlers

  //region Effects
  useEffect(() => {
    if (!canvasRef.current) return;

    const app = new Application(canvasRef.current);
    appRef.current = app;

    app.load(settings.url)
      .then(() => {
        switchLayout(app, windowState.orientation);
      })
      .catch(err => console.error('Spline load error', err));

    return () => {
      app.dispose();
    };
  }, [canvasKey]);

  useEffect(() => {
    if (!containerRef.current || !appRef.current) return;

    const resize = () => {
      const rect = containerRef.current!.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        appRef.current!.setSize(rect.width, rect.height);
      }
    };

    resize();
    window.addEventListener('resize', resize);

    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    if (!windowState?.orientation) return;
    const nextOrientation = windowState.orientation;

    if (!appRef.current) return;
    switchLayout(appRef.current, nextOrientation);
  }, [windowState.orientation]);

  useEffect(() => {
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
  //endregion Effects

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: `${window.innerHeight}px`,
        ...style
      }}
      className={className}
    >
      <canvas
        key={canvasKey}
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          touchAction: 'none'
        }}
      />
    </div>
  );
};

export default Spline;